import tempMeeApi from '../../../apis/tempMee';

export const INTERNAL_USERS_ENDPOINT = '/auth-service/admin/internal-users';

/** The endpoint's own default page size, and what the GoTu Admin list shows. */
export const INTERNAL_USERS_DEFAULT_LIMIT = 15;

/**
 * Hard server-side cap — auth-service clamps anything larger, so a caller
 * asking for more than this truncates silently rather than erroring.
 */
export const INTERNAL_USERS_MAX_LIMIT = 100;

/**
 * The GoTu Admin list query: `GET /admin/internal-users` on auth-service.
 *
 * One client for both consumers of this endpoint — the paginated GoTu Admin
 * page and the single-role dropdown lists (see GetInternalUsersByRole, which
 * wraps this) — so the auth header and param spelling live in one place.
 *
 * Falsy filters are omitted rather than sent empty. `role=` in particular would
 * put '' into auth-service's `internal_role IN (...)`, which matches nothing
 * and blanks the page.
 *
 * `sortBy` defaults to first_name because the endpoint's own default is
 * created_at, and the backfill gave every pre-existing admin a byte-identical
 * created_at — leaving the default in place would order the list arbitrarily.
 *
 * @param {Object} [params]
 * @param {number} [params.page] - 1-based page; omitted when falsy.
 * @param {number} [params.limit=15] - Page size, capped at 100 server-side.
 * @param {string} [params.search] - Matches name or email.
 * @param {string[]} [params.roles] - Role slugs, OR'd by the server.
 * @param {string} [params.sortBy='first_name'] - created_at|first_name|last_name|email.
 * @param {string} [params.orderBy='asc'] - asc|desc.
 * @returns {Promise<import('../types/User.api').IGetInternalUsersApiResponse>}
 */
export const getInternalUsers = async ({
  page,
  limit = INTERNAL_USERS_DEFAULT_LIMIT,
  search,
  roles,
  sortBy = 'first_name',
  orderBy = 'asc',
} = {}) => {
  const params = { limit, sortBy, orderBy };
  if (page) params.page = page;
  if (search) params.search = search;
  // Comma-separated rather than a repeated param: auth-service's parseRoles
  // accepts both, and one value is simpler to build with axios params.
  if (roles?.length) params.role = roles.join(',');

  const response = await tempMeeApi.get(INTERNAL_USERS_ENDPOINT, {
    params,
  });

  return response.data;
};
